/* ===================================================================
   MobileNav -- bottom tab bar for small screens (<840px), ported from the
   Claude Design prototype ("Rhody Reviews.dc.html") bottom tab bar (@2795).
   The prototype shows FIVE tabs on phones: Home / Reviews / Requests /
   Customers / More. "More" opens the full secondary-nav menu (/more), and
   its tab is active whenever the current route is a secondary destination
   (anything not one of the four primary tabs).

   Rendered by AppShell; display:none above 840px (CSS), where the sidebar
   takes over. Active state is driven by the current route.
   =================================================================== */
import { NavLink, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import '../styles/mobilenav.css';

const I = (d: ReactNode) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9}>
    {d}
  </svg>
);

type Tab = { to: string; label: string; icon: ReactNode };

/* The four primary tabs, matching the prototype bottom bar order. */
const TABS: Tab[] = [
  { to: '/dashboard', label: 'Home', icon: I(<><rect x="3" y="3" width="7" height="9" rx="1.5" /><rect x="14" y="3" width="7" height="5" rx="1.5" /><rect x="14" y="12" width="7" height="9" rx="1.5" /><rect x="3" y="16" width="7" height="5" rx="1.5" /></>) },
  { to: '/reviews', label: 'Reviews', icon: I(<path d="M21 11.5a8.4 8.4 0 01-9 8 8 8 0 01-3.5-.7L3 20l1.3-4A8.4 8.4 0 1121 11.5z" strokeLinejoin="round" />) },
  { to: '/requests', label: 'Requests', icon: I(<path d="M4 5h16v11H7l-3 3z" strokeLinejoin="round" />) },
  { to: '/customers', label: 'Customers', icon: I(<><circle cx="9" cy="8" r="3.2" /><path d="M3 20c0-3.3 2.7-5.5 6-5.5s6 2.2 6 5.5M17 9a2.8 2.8 0 000-5.5M18 20c0-2.5-1-4.2-2.5-5.2" strokeLinecap="round" strokeLinejoin="round" /></>) },
];

/* The "More" tab is active for /more AND every secondary destination (the
   screens that live inside the More menu on mobile). */
const PRIMARY = new Set(TABS.map((t) => t.to));
const MoreDots = I(<><circle cx="5" cy="12" r="2" fill="currentColor" stroke="none" /><circle cx="12" cy="12" r="2" fill="currentColor" stroke="none" /><circle cx="19" cy="12" r="2" fill="currentColor" stroke="none" /></>);

export function MobileNav() {
  const { pathname } = useLocation();
  const moreActive =
    pathname.startsWith('/more') ||
    (![...PRIMARY].some((p) => pathname.startsWith(p)) && pathname !== '/');

  return (
    <nav className="mobnav" aria-label="Primary">
      {/* All four primary tabs are the desktop MAIN section, so they carry
          MAIN's hue (gold = earned / reputation). Same mapping as the sidebar
          rail -- see "THE NAV SECTION MAPPING" in styles/tokens.css. */}
      {TABS.map((t) => (
        <NavLink
          key={t.to}
          to={t.to}
          className={({ isActive }) => 'mobtab hue-gold' + (isActive ? ' is-active' : '')}
        >
          {t.icon}
          <span>{t.label}</span>
        </NavLink>
      ))}
      {/* "More" is the doorway to BOTH Automate (blue) and Grow (green), so it
          belongs to no single hue and stays neutral. Picking one would lie
          about where the tab goes. */}
      <NavLink to="/more" className={'mobtab hue-none' + (moreActive ? ' is-active' : '')}>
        {MoreDots}
        <span>More</span>
      </NavLink>
    </nav>
  );
}
